'use client'

import * as React from 'react'
import { AnimatedSpinnerIcon } from 'primitives/icons/animated-spinner-icon'
import { tw } from 'utils/classnames'
import { useIsLoggedInQuery } from 'auth/hooks'
import { sendAmplitudeEvent, AmplitudeEvents } from '@repo/analytics/amplitude/utils'
import type { CoinPack } from '../gen/types'
import { PaymentStatus } from '../gen/types'
import * as Dialog from 'primitives/dialog'
import { Z_INDEX_MODALS } from 'constants/modals'
import { IconButton } from 'primitives/buttons/icon-button'
import { XCloseIcon } from 'primitives/icons'
import { defaultLanguage } from 'cf-i18n/constants'
import { useLangCookie } from 'cf-i18n/cookies'

import i18n from './i18n/i18n.json'
import type { TopUpReturn } from './hooks/use-coins-top-up'
import { useCoinsTopUp } from './hooks/use-coins-top-up'

interface CoinsTopUpButtonProps {
  style?: React.CSSProperties
  label: string | React.ReactNode
  className?: string
  onSuccess?: () => void
  coinPack: CoinPack
}

// oxlint-disable-next-line complexity
export function CoinsTopUpButton({
  label,
  className,
  onSuccess,
  style = {},
  coinPack,
}: CoinsTopUpButtonProps) {
  const isLoggedInQuery = useIsLoggedInQuery()
  const [variant, setVariant] = React.useState<TopUpReturn | null>(null)

  const [langCookie] = useLangCookie()
  const [locale, setLocale] = React.useState(defaultLanguage)
  const expectedLocale = langCookie || defaultLanguage

  React.useEffect(() => {
    setLocale(expectedLocale)
  }, [expectedLocale])

  const t = i18n[locale]

  const { buyCoins, isPending } = useCoinsTopUp()

  const handleBuyCoinPacksClick = React.useCallback(
    async (coinPack: CoinPack) => {
      sendAmplitudeEvent(AmplitudeEvents.COIN_PACK_PURCHASE_CLICKED, {
        coin_pack: coinPack,
      })

      const response = await buyCoins({ coinPack })

      if (!response) {
        return
      }

      if (response.paymentsStatus === PaymentStatus.Succeeded) {
        onSuccess?.()
      }

      setVariant(response)
    },
    [buyCoins, onSuccess],
  )

  if (!isLoggedInQuery.data) {
    return null
  }

  const closeModal = () => {
    setVariant(null)
  }

  return (
    <>
      <button
        style={style}
        onClick={() => handleBuyCoinPacksClick(coinPack)}
        disabled={isPending}
        className={tw('relative flex items-center justify-center', className)}
      >
        <div
          className={isPending ? 'invisible' : 'visible'}
          {...(typeof label === 'string' ? { dangerouslySetInnerHTML: { __html: label } } : {})}
        >
          {typeof label !== 'string' ? label : null}
        </div>
        {isPending ? (
          <span className="absolute inset-0 flex items-center justify-center">
            <AnimatedSpinnerIcon />
          </span>
        ) : null}
      </button>

      <Dialog.Root
        open={Boolean(variant && variant.paymentsStatus !== PaymentStatus.Succeeded)}
        onOpenChange={() => {
          closeModal()
        }}
        defaultOpen={false}
      >
        <Dialog.Portal>
          <div data-cf-external>
            <Dialog.Body dismissable={false} zIndex={Z_INDEX_MODALS.oneClickUpgrade.modal}>
              <div className="font-poppins relative flex w-full max-w-[450px] flex-col items-center rounded-[12px] bg-[#ffffff] p-5 text-[#393D56]">
                {variant?.error ? (
                  <h3 className="text-center text-[16px] font-bold">
                    {t.messages.purchase_failed}
                  </h3>
                ) : null}

                {variant?.paymentsStatus === PaymentStatus.Processing ? (
                  //payment processing
                  <h3 className="text-center text-[16px] font-bold">{t.messages.processing}</h3>
                ) : null}

                {variant?.paymentsStatus === PaymentStatus.RequiresAction ? (
                  //Requires action
                  <h3 className="flex flex-col text-center text-[16px] font-bold">
                    {variant.message ? t.messages.requires_action : t.messages.purchase_failed}
                    {variant.message ? (
                      <a
                        className="underline"
                        target="_blank"
                        href={variant.message || ''}
                        rel="noreferrer"
                      >
                        {t.messages.cta}
                      </a>
                    ) : null}
                  </h3>
                ) : null}

                {variant?.paymentsStatus === PaymentStatus.Failed ? (
                  //payment failed
                  <h3 className="flex flex-col text-center text-[16px] font-bold">
                    {variant?.message ? (
                      <>
                        {t.messages.purchase_failed_1}
                        <a
                          className="underline"
                          target="_blank"
                          href={(variant && variant.message) || ''}
                          rel="noreferrer"
                        >
                          {t.messages.cta}
                        </a>
                      </>
                    ) : (
                      <>
                        {t.messages.invalid_payment}
                        <a
                          className="underline"
                          target="_blank"
                          href={'/my-account/payment-methods/'}
                          rel="noreferrer"
                        >
                          {t.messages.click_here}
                        </a>
                      </>
                    )}
                  </h3>
                ) : null}

                <div className="absolute right-[-18px] top-[-18px] [@media(min-width:1340px)]:-top-[52px] [@media(min-width:1340px)]:translate-y-full">
                  <Dialog.Close asChild>
                    <IconButton
                      variant="violet"
                      className="h-[36px] w-[36px] rounded-[50%] transition-transform hover:scale-110 active:scale-100"
                    >
                      <img
                        src={XCloseIcon.src}
                        width="30"
                        height="30"
                        className="h-[30px] w-[30px] [filter:invert(1)]"
                      />
                    </IconButton>
                  </Dialog.Close>
                </div>
              </div>
            </Dialog.Body>
          </div>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  )
}
